import type { PlanChangePreview } from '../../client'
import { formatCredits, formatDate } from '../../lib/format'
import Spinner from '../chat/Spinner'

const money = (amount: number) => `$${(amount / 100).toLocaleString()}`

type Props = {
  planName: string
  preview: PlanChangePreview | null
  loading: boolean
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-6 py-2.5 text-sm">
      <span className="text-stone-500">{label}</span>
      <span className="font-medium text-stone-900">{value}</span>
    </div>
  )
}

export default function PlanChangeDialog({
  planName,
  preview,
  loading,
  busy,
  error,
  onConfirm,
  onCancel,
}: Props) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Change to ${planName}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="font-heading text-xl font-semibold text-stone-900">Change to {planName}</h2>

        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-5 border-stone-300 border-t-stone-500" />
          </div>
        ) : preview ? (
          <>
            <div className="mt-4 divide-y divide-stone-200 border-y border-stone-200">
              <Row label="New allowance" value={`${formatCredits(preview.credits)} credits / month`} />
              <Row label="Plan price" value={`${money(preview.amount)} / ${preview.interval}`} />
              <Row label="Due today" value={money(preview.amount_due_now)} />
              {preview.next_charge_at && (
                <Row label="Next charge" value={formatDate(preview.next_charge_at)} />
              )}
            </div>
            <p className="mt-4 text-sm text-stone-500">
              Credits you have already used stay used. Your allowance moves to{' '}
              {formatCredits(preview.credits)} straight away, and top-up credits are not affected.
            </p>
          </>
        ) : (
          <p className="mt-4 text-sm text-stone-500">
            We could not price this change. Try again, or manage the plan in the billing portal.
          </p>
        )}

        {error && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-stone-300 bg-white px-4 text-sm font-medium text-stone-900 transition-colors enabled:hover:bg-stone-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || loading || !preview}
            className="inline-flex h-10 items-center justify-center rounded-xl bg-linear-to-b from-stone-700 to-stone-950 px-4 text-sm font-medium text-white transition-opacity enabled:hover:opacity-90 disabled:opacity-60"
          >
            {busy ? 'Changing…' : 'Confirm change'}
          </button>
        </div>
      </div>
    </div>
  )
}
